<template>
  <div class="c-product-card">
    <a
      :class="['c-product-card__link', { widthFixed, narrow }]"
      :href="product.url"
      :aria-label="product.plainTitle"
    >
      <div class="c-product-card__rank-badge">
        <f-product-card-rank-badge
          v-if="showRank"
          :rank-number="rankNumber"
          :size="narrow ? 'small' : ''"
        ></f-product-card-rank-badge>
      </div>

      <div class="c-product-card__status-label" :class="{ narrow }">
        <f-product-card-status-label
          :product-status="product.productStatus"
          :type="narrow ? 'badge' : 'triangle'"
        ></f-product-card-status-label>
      </div>
      <div>
        <f-product-image
          :product="product"
          :showFavoriteButton="showFavoriteButton"
          :wideContent="wideContent"
          :widthFixed="widthFixed"
        ></f-product-image>

        <div class="c-product-card__content" :class="{ narrow, wideContent }">
          <h3
            class="c-product-card__title"
            :class="{ inHeaderMenu }"
            v-html="title"
          ></h3>
          <div class="c-product-card__review" v-if="showReview">
            <review-summary :product="product" />
          </div>
        </div>
      </div>

      <div class="c-product-card__content" :class="{ narrow, wideContent }">
        <div class="c-product-card__price-area">
          <f-product-card-price-area
            :product="product"
            :size="narrow ? 'small' : ''"
            :show-discount-rate="showDiscountRate"
          />
        </div>

        <div v-if="showCartButton" class="c-product-card__button">
          <f-product-card-button-area :product="product" />
        </div>

        <div v-if="showProductListButton" class="c-product-card__button">
          <f-button
            size="small"
            html-tag="button"
            type="button"
            outline
            @button-clicked.stop.prevent="onAddProductListButtonClick"
          >
            <i class="c-product-card__icon-present icon-present"></i>
            {{ addProductListButtonText }}
          </f-button>
        </div>
        <div v-else-if="showAddProductListButton && !showRemoveProductListButton" class="c-product-card__button-empty-space"></div>
      </div>
    </a>
  </div>
</template>

<script>
import { pushProductClickEvent } from '~/js/gtm'
import { ProductListService } from '~/js/services/productListService'

import Product from '~/js/models/product'
import OSProduct from '~/js/models/OSProduct'

import FButton from '~/js/components/button'
import FProductImage from '~/js/components/productImage'
import FProductCardButtonArea from '~/js/components/productCard/buttonArea'
import FProductCardPriceArea from '~/js/components/productCard/priceArea'
import FProductCardRankBadge from '~/js/components/productCard/rankBadge'
import FProductCardStatusLabel from '~/js/components/productCard/statusLabel'
import ReviewSummary from '~/js/components/reviewSummary/index.vue'

export default {
  name: 'ComponentProductCard',
  components: {
    FButton,
    FProductImage,
    FProductCardButtonArea,
    FProductCardPriceArea,
    FProductCardRankBadge,
    FProductCardStatusLabel,
    ReviewSummary,
  },
  props: {
    isInProductList: { type: Boolean, default: true },
    narrow: { type: Boolean, default: false },
    markWord: { type: String, default: '' },
    product: {
      type: [Product, OSProduct],
      required: true,
      default: () => {
        return new Product()
      },
    },
    productListId: { type: String, default: '' },
    productListTitle: { type: String, default: 'ほしいものリスト' },
    rankNumber: { type: [Number, null], default: null },
    showRank: { type: Boolean, default: false },
    showReview: { type: Boolean, default: true },
    showAddProductListButton: { type: Boolean, default: false },
    showRemoveProductListButton: { type: Boolean, default: false },
    showCartButton: { type: Boolean, default: true },
    showFavoriteButton: { type: Boolean, default: true },
    showDiscountRate: { type: Boolean, default: false },
    wideContent: { type: Boolean, default: false },
    widthFixed: { type: Boolean, default: false },
  },
  computed: {
    addProductListButtonText() {
      return this.isInProductList
        ? `${this.productListTitle}から削除`
        : `${this.productListTitle}に追加`
    },
    inHeaderMenu() {
      return this.$root.$options.name === 'SnippetsHeader'
    },
    showProductListButton () {
      return this.showAddProductListButton && !this.isInProductList || this.showRemoveProductListButton && this.isInProductList
    },
    title() {
      return this.product.enhancedTitle
    },
  },
  created() {
    this.productListService = new ProductListService()
  },
  methods: {
    onProductCardClick() {
      pushProductClickEvent({
        product: this.product,
        position: this.rankNumber,
      })
    },
    async onAddProductListButtonClick() {
      try {
        if (this.isInProductList) {
          if (
            !confirm(
              `商品を${this.productListTitle}から削除します。よろしいですか？`
            )
          )
            return
          await this.productListService.removeProduct({
            productListId: this.productListId,
            productId: this.product.id,
          })
          this.$emit('product-removed-from-list', this.product.id)
        } else {
          await this.productListService.addProduct({
            productId: this.product.id,
          })
          this.$emit('product-added-to-list', this.product.id)
        }
      } catch (error) {
        console.error(error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use 'sass:color';
@use '~/scss/generic/variables';
@use '~/scss/generic/mixins';

.c-product-card {
  height: 100%;
  position: relative;

  &__rank-badge {
    left: 8px;
    position: absolute;
    top: 8px;
    z-index: 1;
  }

  &__status-label {
    position: absolute;
    right: 0;
    top: 0;
    z-index: 1;

    &.narrow {
      top: 8px;
      left: 8px;
      right: auto;
    }
  }

  &.narrow {
    width: calc(100% / 3);
    padding: 4px;
  }

  &__link {
    background-color: #fff;
    display: flex;
    height: 100%;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
    padding: 8px 8px 12px;
    position: relative;
    transition: background-color 0.3s;

    &:hover {
      background-color: color.adjust(variables.$color-pink, $lightness: 4%);
    }

    &.widthFixed {
      width: variables.$width-product-card-default;
    }

    @include mixins.screen-mobile {
      padding: 8px;
    }

    &:not(.narrow) {
      border-radius: 8px;
      min-width: variables.$width-product-card-default;
    }
  }

  &__content {
    padding: 0 12px;

    @include mixins.screen-mobile {
      &.narrow,
      &.wideContent {
        padding: 0;
      }
    }

    > * + * {
      margin-top: 8px;
    }
  }

  &__title {
    display: -webkit-box;
    flex-grow: 1;
    font-size: 13px;
    font-weight: 600;
    height: auto;
    margin-top: 0;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;

    @include mixins.screen-desktop {
      &:not(.inHeaderMenu) {
        height: 2em * 1.5;
        font-size: 16px;
        -webkit-line-clamp: 2;
      }
    }

    :deep(em) {
      background-color: variables.$color-pink;
      padding: 2px;
      font-style: inherit;
    }

    &.narrow {
      font-size: 12px;
      line-height: 1.7;
    }
  }

  &__series-name {
    color: variables.$color-brown;
    font-size: 13px;
    font-weight: 600;

    @include mixins.screen-desktop {
      font-size: 16px;
    }
  }

  &__review {
    margin: 4px 0;
    min-height: 18px;
  }

  &__icon-present {
    font-size: 15px;
    line-height: 1;
    margin-right: 4px;

    @include mixins.screen-mobile {
      display: none;
    }
  }

  &__button-empty-space {
    height: 25px;
  }

  :deep(.c-button) {
    margin-right: 8px;

    @include mixins.screen-mobile {
      min-width: 92px;
      width: 100%;
    }
  }

  :deep(.c-button) {
    padding-right: 28px;
    padding-left: 28px;
    white-space: nowrap;
  }
}
</style>
